import { TaskTypeStatus, type ProcessTemplateTaskType } from '@econtractor/types';
import { getProcessNextTasks, getProcessRootTasks, processTaskInstanceKey } from './nextTask';

export type ProcessDiagramEdgeRole = 'active' | 'inactive' | 'normal';

type DiagramTask = ProcessTemplateTaskType & {
    instanceStatus?: number | null;
    instanceAnswerKey?: number | null;
    instancePending?: boolean;
};

const taskKey = (task: Pick<ProcessTemplateTaskType, 'templateId' | 'dtId'>): string =>
    processTaskInstanceKey(task.templateId, task.dtId);

export const isProcessDiagramTaskReached = (task: DiagramTask): boolean =>
    task.instancePending === true ||
    (task.instanceStatus != null && Number.isFinite(Number(task.instanceStatus)));

const linkMatchesAcceptedAnswer = (task: DiagramTask, answerKey: number | null): boolean => {
    if (task.instanceStatus !== TaskTypeStatus.ACCEPTED) return true;
    const hasAnswers = (task.answers || []).length > 0;
    if (!hasAnswers) return answerKey == null;
    return answerKey == null || answerKey === (task.instanceAnswerKey ?? null);
};

const edgeToken = (sourceKey: string, targetKey: string, answerKey: number | null): string =>
    `${sourceKey}>${targetKey}:${answerKey ?? 'none'}`;

export interface ProcessDiagramProgress {
    prunedKeys: Set<string>;
    currentKeys: Set<string>;
    edgeRole: (sourceKey: string, targetKey: string, answerKey: number | null) => ProcessDiagramEdgeRole;
}

/** Жива гілка, відсіяні задачі і ребро до поточної позиції. */
export const classifyProcessDiagram = (tasks: DiagramTask[]): ProcessDiagramProgress => {
    const prunedKeys = new Set<string>();
    const currentKeys = new Set<string>();
    const roles = new Map<string, ProcessDiagramEdgeRole>();

    if (!tasks.some(isProcessDiagramTaskReached)) {
        return { prunedKeys, currentKeys, edgeRole: () => 'normal' };
    }

    const byKey = new Map(tasks.map((task) => [taskKey(task), task]));
    const live = new Set<string>();
    const queue = getProcessRootTasks(tasks).map(taskKey);

    while (queue.length > 0) {
        const key = queue.shift();
        if (key == null || live.has(key)) continue;
        const task = byKey.get(key);
        if (!task) continue;
        live.add(key);

        const links = getProcessNextTasks(task.processData);
        const nextLinks =
            task.instanceStatus === TaskTypeStatus.ACCEPTED
                ? links.filter((link) => linkMatchesAcceptedAnswer(task, link.answerKey))
                : links;
        for (const link of nextLinks) {
            const targetKey = processTaskInstanceKey(link.templateId, link.dtId);
            if (live.has(targetKey) || !byKey.has(targetKey)) continue;
            queue.push(targetKey);
        }
    }

    for (const task of tasks) {
        const key = taskKey(task);
        if (!live.has(key)) prunedKeys.add(key);
    }

    const activeSources = new Set<string>();
    for (const task of tasks) {
        const sourceKey = taskKey(task);
        for (const link of getProcessNextTasks(task.processData)) {
            const targetKey = processTaskInstanceKey(link.templateId, link.dtId);
            const target = byKey.get(targetKey);
            if (!target) continue;
            const kept = linkMatchesAcceptedAnswer(task, link.answerKey);
            let role: ProcessDiagramEdgeRole = 'normal';
            if (prunedKeys.has(sourceKey) || prunedKeys.has(targetKey) || (task.instanceStatus != null && !kept)) {
                role = 'inactive';
            } else if (task.instanceStatus === TaskTypeStatus.ACCEPTED && kept && isProcessDiagramTaskReached(target)) {
                role = 'active';
                activeSources.add(sourceKey);
            }
            roles.set(edgeToken(sourceKey, targetKey, link.answerKey), role);
        }
    }

    for (const task of tasks) {
        const key = taskKey(task);
        if (prunedKeys.has(key) || activeSources.has(key) || !isProcessDiagramTaskReached(task)) continue;
        currentKeys.add(key);
    }

    return {
        prunedKeys,
        currentKeys,
        edgeRole: (sourceKey, targetKey, answerKey) => roles.get(edgeToken(sourceKey, targetKey, answerKey)) ?? 'normal',
    };
};
